import { FC, memo, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useSitemap } from 'shared/hooks/useSitemap';
import { ConnectisLogo } from 'shared/icons/ConnectisLogo';
import { mergeClasses } from 'shared/utils/mergeClasses';

const SidePane: FC<{ children: ReactNode; className: string }> = memo(
  ({ children, className = '' }) => {
    const sitemap = useSitemap();

    return (
      <div
        className={mergeClasses(
          'sticky top-0 order-2 bg-custom-gradient px-4 py-12 pb-7 lg:order-1 lg:col-span-3 lg:h-full-device lg:p-12',
          className,
        )}
      >
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute left-0 top-0 z-0 hidden h-full w-full bg-ellipse-middle bg-cover bg-no-repeat lg:block"
            aria-hidden="true"
          />
          <div
            className="absolute left-0 top-0 z-0 hidden h-full w-full bg-ellipse-bottom bg-cover bg-no-repeat lg:block"
            aria-hidden="true"
          />
          <div
            className="absolute left-0 top-0 z-0 hidden h-full w-full bg-ellipse-tl bg-cover bg-no-repeat lg:block"
            aria-hidden="true"
          />

          <div
            className="absolute left-0 top-0 z-0 h-full w-full bg-mobile-ellipse-middle bg-cover bg-no-repeat lg:hidden"
            aria-hidden="true"
          />
          <div
            className="absolute left-0 top-0 z-0 h-full w-full bg-mobile-ellipse-tl bg-cover bg-no-repeat lg:hidden"
            aria-hidden="true"
          />
        </div>
        <div className="flex h-full flex-col">
          <div className="relative z-20 mb-[90px] hidden lg:flex">
            <Link to={sitemap.main}>
              <ConnectisLogo className="w-[120px] lg:w-[136px]" />
            </Link>
          </div>
          <div className="z-10 flex h-full w-full flex-col">{children}</div>
        </div>
      </div>
    );
  },
);

const MainContent: FC<{ children: ReactNode }> = memo(({ children }) => {
  return (
    <div className="order-1 bg-white p-0 lg:order-2 lg:col-span-5 lg:bg-lightBlue lg:p-8">
      {children}
    </div>
  );
});

interface SidePaneLayoutProps {
  children: ReactNode;
  sidePaneChildren: ReactNode;
  className?: string;
  sidePaneClassName?: string;
}

export const SidePaneLayout: FC<SidePaneLayoutProps> = memo(
  ({ children, sidePaneChildren, className = '', sidePaneClassName = '' }) => {
    const sitemap = useSitemap();

    return (
      <main
        className={mergeClasses(
          'relative mx-auto grid grid-cols-1 lg:min-h-full-device lg:grid-cols-8',
          className,
        )}
      >
        <div className="order-0 sticky top-0 z-20 bg-custom-gradient-mobile bg-[length:105.33%_289.29%] bg-[position:54%_-68px] lg:hidden lg:bg-custom-gradient lg:bg-auto lg:bg-center">
          <div className="relative z-10 flex h-full w-full flex-col px-4 pb-6 pt-4">
            <Link to={sitemap.main}>
              <ConnectisLogo className="flex w-[120px]" />
            </Link>
          </div>
        </div>
        {sidePaneChildren && <SidePane className={sidePaneClassName}>{sidePaneChildren}</SidePane>}
        <MainContent>{children}</MainContent>
      </main>
    );
  },
);
